import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Stack,
  Button,
  Chip,
  Avatar,
} from "@material-ui/core";
import { LoadingButton } from "@material-ui/lab";
import { Icon } from "@iconify/react";
import backspaceIcon from "@iconify-icons/eva/backspace-fill";
import personIcon from "@iconify-icons/eva/person-fill";
import lockIcon from "@iconify-icons/eva/lock-fill";
import DialogAnimate from "src/components/Animate/DialogAnimate";
import { useDispatch, useSelector } from "src/redux/store";
import {
  loginByPin,
  closePinDialog,
  hasError,
} from "src/redux/slices/employeeSession";
import { getDeviceId } from "src/utils/deviceId";
import { useSnackbar } from "notistack";
import useEmployeeSessionKeepAlive from "src/hooks/useEmployeeSessionKeepAlive";
import createAvatar from "src/utils/createAvatar";

// F4 · Diálogo PIN reusable. Tres modos:
//
//  1) Standalone (login inicial): `<PinLoginDialog open onClose />` desde
//     cualquier vista y decides tú cuándo se cierra.
//
//  2) Global (RequirePin): `dispatch(openPinDialog({ reason, action,
//     onSuccess, onCancel }))` y el overlay <PinSessionOverlay/> lo pinta.
//
//  3) Global con selector (supervisor override): `dispatch(openPinDialog({
//     showEmployeeSelector: true, reason, onSuccess }))`. Antes del
//     teclado, el usuario ELIGE qué empleado dice ser (grid de avatares).
//     El employeeId elegido viaja al backend, que valida el PIN contra
//     ESE empleado (no contra cualquiera que lo comparta).

const KEYPAD = [
  ["1", "2", "3"],
  ["4", "5", "6"],
  ["7", "8", "9"],
  ["clear", "0", "back"],
];

export default function PinLoginDialog({
  open,
  onClose,
  reason,
  action,
  onSuccess,
  onCancel,
  showEmployeeSelector = false,
}) {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { isLoading, error } = useSelector((s) => s.employeeSession);
  // Empleados del restaurante para pintar el selector. Ya cargados en el
  // slice `employees` por el layout; si aún no están se pinta vacío y el
  // hook `useEffect` de GetAllEmployees los rellena.
  const { employees } = useSelector((s) => s.employees);

  const [pin, setPin] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // Fase actual del diálogo. Con selector, arrancamos en "pick"; sin
  // selector, directo al keypad.
  const phase = showEmployeeSelector && !selectedEmployee ? "pick" : "pin";

  useEffect(() => {
    if (open) {
      setPin("");
      setSelectedEmployee(null);
      // El chip rojo de PIN incorrecto vive en el slice `employeeSession.error`.
      // Sin este reset queda pegado entre aperturas y contamina la UX
      // cuando el usuario reabre el modal para reintentar.
      dispatch(hasError(null));
    }
  }, [open, dispatch]);

  const handleClose = () => {
    setPin("");
    setSelectedEmployee(null);
    dispatch(hasError(null));
    onCancel?.();
    onClose?.();
  };

  const handleSubmit = async () => {
    if (pin.length < 3) {
      enqueueSnackbar("PIN demasiado corto", { variant: "warning" });
      return;
    }
    try {
      // Cuando el diálogo se abrió con `showEmployeeSelector` (flujo
      // override supervisor o useRequirePin), el login es EFÍMERO —
      // se cerrará inmediatamente después de reejecutar la acción y
      // NO debe pisar la sesión del empleado que estaba operando el
      // TPV. Enviamos `preserveOtherSessions=true` para que el
      // backend NO revoque las sesiones activas del device.
      const res = await dispatch(
        loginByPin(
          pin,
          getDeviceId(),
          selectedEmployee?.id ?? null,
          !!showEmployeeSelector,
        ),
      );

      // El backend puede devolver AMBIGUOUS_PIN cuando no pasamos
      // employeeId y hay >1 candidato. En ese caso pintamos la lista
      // como selector — el usuario elige quién es y reenviamos.
      if (res?.code === "AMBIGUOUS_PIN") {
        const candidates = res.candidates || [];
        if (candidates.length === 0) {
          enqueueSnackbar("PIN incorrecto", { variant: "error" });
          setPin("");
          return;
        }
        // Reusa el flujo del selector con la lista devuelta por el
        // backend. Aunque no vinieran con avatar, mostramos iniciales.
        setAmbiguousCandidates(candidates);
        setPin("");
        return;
      }

      enqueueSnackbar(`Bienvenido ${res?.session?.name ?? ""}`.trim(), {
        variant: "success",
      });
      setPin("");
      setSelectedEmployee(null);
      onSuccess?.(res);
      onClose?.();
    } catch (err) {
      const msg =
        typeof err === "string"
          ? err
          : err?.response?.data?.message
              || err?.message
              || "PIN incorrecto";
      enqueueSnackbar(msg, { variant: "error" });
    }
  };

  const [ambiguousCandidates, setAmbiguousCandidates] = useState(null);

  const handleKey = (key) => {
    if (key === "back") return setPin((p) => p.slice(0, -1));
    if (key === "clear") return setPin("");
    if (pin.length >= 8) return;
    setPin((p) => p + key);
  };

  // Hardware keyboard support en tablet con teclado externo.
  useEffect(() => {
    if (!open || phase !== "pin") return undefined;
    const onKey = (e) => {
      if (e.key >= "0" && e.key <= "9") {
        e.preventDefault();
        handleKey(e.key);
      } else if (e.key === "Backspace") {
        e.preventDefault();
        handleKey("back");
      } else if (e.key === "Enter") {
        e.preventDefault();
        handleSubmit();
      } else if (e.key === "Escape") {
        e.preventDefault();
        handleClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, phase, pin]);

  const dotColor = useMemo(() => {
    if (error) return "error.main";
    if (pin.length >= 4) return "success.main";
    return "primary.main";
  }, [error, pin]);

  // ── Sub-renders ────────────────────────────────────────────────
  const renderEmployeePicker = () => {
    const list = ambiguousCandidates
      ?? employees.filter((e) => e.isActive && !e.isAdmin);

    return (
      <Box sx={{ p: 3 }}>
        <Stack spacing={0.5} alignItems="center" sx={{ mb: 2 }}>
          <Box
            sx={{
              width: 56, height: 56, borderRadius: "50%",
              bgcolor: "primary.lighter", color: "primary.main",
              display: "flex", alignItems: "center", justifyContent: "center",
              mb: 1,
            }}
          >
            <Icon icon={personIcon} width={28} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            {ambiguousCandidates
              ? "¿Cuál eres tú?"
              : "¿Qué encargado autoriza?"}
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            {ambiguousCandidates
              ? "Ese PIN identifica a varios empleados. Elige tu perfil."
              : reason || "Selecciona tu perfil para autorizar la acción"}
          </Typography>
        </Stack>

        {list.length === 0 ? (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
            No hay empleados disponibles.
          </Typography>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))",
              gap: 1.5,
              maxHeight: 320,
              overflowY: "auto",
              pb: 1,
            }}
          >
            {list.map((e) => {
              const av = createAvatar(e.name || "?");
              return (
                <Box
                  key={e.id}
                  component="button"
                  type="button"
                  onClick={() => {
                    setSelectedEmployee(e);
                    setAmbiguousCandidates(null);
                    setPin("");
                    // Al elegir usuario limpiamos el chip "PIN incorrecto"
                    // heredado del intento anterior — si no queda visible
                    // mientras tecleo el PIN del empleado recién elegido.
                    dispatch(hasError(null));
                  }}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 0.75,
                    p: 1,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    bgcolor: "background.paper",
                    cursor: "pointer",
                    transition: "all 120ms",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: 2,
                      borderColor: "primary.main",
                    },
                  }}
                >
                  <Avatar
                    src={e.avatar || undefined}
                    sx={{
                      width: 48,
                      height: 48,
                      bgcolor: av?.color2 || "primary.main",
                      color: "#fff",
                      fontSize: 18,
                      fontWeight: 700,
                    }}
                  >
                    {av?.name}
                  </Avatar>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      textAlign: "center",
                      lineHeight: 1.2,
                      wordBreak: "break-word",
                    }}
                  >
                    {e.name}
                  </Typography>
                  {e.roleName && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                      {e.roleName}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        )}

        <Button
          fullWidth
          size="large"
          variant="text"
          onClick={handleClose}
          sx={{ mt: 2 }}
        >
          Cancelar
        </Button>
      </Box>
    );
  };

  const renderPinKeypad = () => (
    <Box sx={{ p: 3 }}>
      <Stack spacing={0.5} alignItems="center" sx={{ mb: 2 }}>
        {selectedEmployee ? (
          <Avatar
            src={selectedEmployee.avatar || undefined}
            sx={{
              width: 56,
              height: 56,
              bgcolor: "primary.main",
              color: "#fff",
              fontSize: 20,
              fontWeight: 700,
              mb: 1,
            }}
          >
            {(selectedEmployee.name || "?").charAt(0).toUpperCase()}
          </Avatar>
        ) : (
          <Box
            sx={{
              width: 56, height: 56, borderRadius: "50%",
              bgcolor: "primary.lighter", color: "primary.main",
              display: "flex", alignItems: "center", justifyContent: "center",
              mb: 1,
            }}
          >
            <Icon icon={reason ? lockIcon : personIcon} width={28} />
          </Box>
        )}
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          {selectedEmployee
            ? `Introduce el PIN de ${selectedEmployee.name}`
            : reason
              ? "Confirmar con PIN"
              : "Identifícate en el TPV"}
        </Typography>
        {(reason || action) && !selectedEmployee && (
          <Typography variant="body2" color="text.secondary" align="center">
            {reason || `Acción restringida: ${action}`}
          </Typography>
        )}
        {selectedEmployee && showEmployeeSelector && (
          <Button
            size="small"
            variant="text"
            onClick={() => {
              setSelectedEmployee(null);
              setPin("");
              // Reset del chip error al volver al selector — el intento
              // fallido pertenecía al empleado anterior, no debe teñir
              // la nueva elección.
              dispatch(hasError(null));
            }}
          >
            Cambiar usuario
          </Button>
        )}
      </Stack>

      {/* Dots del PIN */}
      <Stack
        direction="row"
        spacing={1.5}
        justifyContent="center"
        sx={{ mb: 2 }}
      >
        {Array.from({ length: Math.max(pin.length, 4) }).map((_, i) => (
          <Box
            key={i}
            sx={{
              width: 14,
              height: 14,
              borderRadius: "50%",
              bgcolor: i < pin.length ? dotColor : "grey.300",
              transition: "all 120ms",
            }}
          />
        ))}
      </Stack>

      {/* Keypad */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 1,
          mb: 2,
        }}
      >
        {KEYPAD.flat().map((k) => (
          <Button
            key={k}
            variant={k === "back" || k === "clear" ? "outlined" : "contained"}
            color={k === "clear" ? "warning" : "primary"}
            onClick={() => handleKey(k)}
            sx={{
              py: 2.5,
              fontSize: 20,
              fontWeight: 700,
              borderRadius: 2,
            }}
          >
            {k === "back" ? (
              <Icon icon={backspaceIcon} width={22} />
            ) : k === "clear" ? (
              "C"
            ) : (
              k
            )}
          </Button>
        ))}
      </Box>

      {error && (
        <Chip
          label={typeof error === "string" ? error : "PIN incorrecto"}
          color="error"
          size="small"
          sx={{ mb: 1, alignSelf: "center", display: "flex" }}
        />
      )}

      <Stack direction="row" spacing={1}>
        <Button
          fullWidth
          size="large"
          variant="text"
          onClick={handleClose}
          disabled={isLoading}
        >
          Cancelar
        </Button>
        <LoadingButton
          fullWidth
          size="large"
          variant="contained"
          loading={isLoading}
          onClick={handleSubmit}
          disabled={pin.length < 3}
        >
          Entrar
        </LoadingButton>
      </Stack>
    </Box>
  );

  return (
    <DialogAnimate
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
    >
      {phase === "pick" ? renderEmployeePicker() : renderPinKeypad()}
    </DialogAnimate>
  );
}

PinLoginDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  reason: PropTypes.string,
  action: PropTypes.string,
  onSuccess: PropTypes.func,
  onCancel: PropTypes.func,
  showEmployeeSelector: PropTypes.bool,
};

// ── Overlay global (opcional): útil para RequirePin ──────────────

export function PinSessionOverlay() {
  const dispatch = useDispatch();
  // 2026-10-02 · Sin cierre por inactividad: el empleado sigue identificado
  // hasta que sale; la sesión se renueva sola (y si el servidor la corta,
  // el TPV vuelve al selector). Se monta aquí porque este overlay ya vive
  // dentro del Provider y se renderiza una única vez.
  useEmployeeSessionKeepAlive();

  const { open, reason, action, onSuccess, onCancel, showEmployeeSelector } =
    useSelector((s) => s.employeeSession.pinDialog);
  return (
    <PinLoginDialog
      open={open}
      onClose={() => dispatch(closePinDialog())}
      reason={reason}
      action={action}
      showEmployeeSelector={showEmployeeSelector}
      onSuccess={(res) => {
        try { onSuccess?.(res); } catch { /* consumer’s bug */ }
        dispatch(closePinDialog());
      }}
      onCancel={() => {
        try { onCancel?.(); } catch { /* consumer’s bug */ }
      }}
    />
  );
}
